import { useEffect, useState } from 'react';
import { generatePath, useParams } from 'react-router';
import {
  Box,
  Button,
  CircularProgress,
  Column,
  Flex,
  Grid,
  IconButton,
  Row,
  Screen,
} from '@tempmee/react';
import {
  OutlinePencilIcon,
  OutlineRepostIcon,
  OutlineTrashIcon,
  OutlineXIcon,
} from '@tempmee/react-icons';
import history from '~/browserHistory';
import { BackButton } from '~/commonComponents';
import { useCancelJobMutation, useGetJob } from '~/domain/Jobs';
import { UserSubProfession } from '~/enums/UserProfession';
import { routes } from '~/routes';
import AnimatedCloseCircle from '../../shared/AnimatedCloseCircle';
import ConfirmPopup from '../../shared/ConfirmPopup';
import LegacyOpenJobScreen from '../OpenJobScreen';
import { InviteSection } from './InviteSection';
import { ShiftDetailsInfo } from './ShiftDetailsInfo';

/**
 * Redesigned open Shift Details page (GOTU-5384, temp shifts). Two-column
 * layout: the inline invite list on the left, the read-only shift details on
 * the right. Gated by the `ShiftDetailsInviteSection` feature flag; when the
 * flag is off the route renders the legacy single-column screen instead.
 *
 * Onsite-interview details are not part of this task (GOTU-5500) and still fall
 * back to the legacy screen until the perm variant (GOTU-5501) ships.
 */
export default function OpenJobScreenRedesign() {
  const params = useParams<{ jobId?: string }>();

  const locationState = history.location.state as
    | { jobId?: string; isJobPost?: boolean }
    | undefined;
  const jobId = locationState ? locationState.jobId : params?.jobId;

  const { data: job = null, isLoading: isJobLoading } = useGetJob(jobId);
  const { mutate: cancelJob, isPending: isCancelPending } = useCancelJobMutation();
  const isLoading = isJobLoading || isCancelPending;

  const [cancelJobPopup, setCancelJobPopup] = useState(false);

  useEffect(() => {
    if (job && (job.status === 'pending' || job.status === 'counter')) {
      const candidateId = job?.multipleCounters?.[0]?.candidate?.id;
      history.replace(
        generatePath(routes.confirmCandidate, { jobId: job.id, candidateId: candidateId ?? '' }),
      );
    } else if (
      job &&
      (job.status === 'filled' || job.status === 'pay-out' || job.status === 'complete')
    ) {
      history.replace(generatePath(routes.jobDetails, { jobId: job.id }));
    }
  }, [job]);

  const repostEfdaAsDa = () => {
    history.push('/dashboard/temp', {
      job: {
        ...job,
        subProfession: null,
      },
    });
  };

  if (isLoading || !job) {
    return (
      <Screen variant="standard">
        <Screen.Body>
          <Flex justify="center" py={10}>
            <CircularProgress />
          </Flex>
        </Screen.Body>
      </Screen>
    );
  }

  // Interview details are still owned by the legacy screen (see GOTU-5501).
  if (job.isOnsiteInterview) {
    return <LegacyOpenJobScreen />;
  }

  const shouldShowRepostButton = job.subProfession === UserSubProfession.EFDA;

  return (
    <>
      <Screen variant="standard">
        <Screen.Header
          start={
            <Box className="-ml-3">
              {locationState?.isJobPost ? (
                <Button
                  variant="text"
                  color="secondary"
                  size="small"
                  startIcon={<OutlineXIcon />}
                  data-cy="shift-details-close"
                  onClick={() => history.push('/dashboard/home')}
                >
                  Close
                </Button>
              ) : (
                <BackButton />
              )}
            </Box>
          }
              title="Shift Details"
              end={
                <Row align="center" gap={10}>
                  <IconButton
                    type="button"
                    color="primary"
                    appearance="filled"
                    size="medium"
                    data-cy="edit-shift-button"
                    aria-label="Edit"
                    onClick={() =>
                      history.push(generatePath(routes.editOpenJob, { jobId: job.id }))
                    }
                    Icon={<OutlinePencilIcon />}
                  >
                    Edit
                  </IconButton>
                  <IconButton
                    type="button"
                    color="primary"
                    appearance="outline"
                    size="medium"
                    data-cy="cancel-shift-button"
                    aria-label="Cancel"
                    onClick={() => setCancelJobPopup(true)}
                    Icon={<OutlineTrashIcon />}
                  >
                    Cancel
                  </IconButton>
                </Row>
              }
        />
        <Screen.Body>
          <Flex justify="center">
            <Box width="min(1200px, 100%)">
              <Grid columns="4fr 3fr" gap={16} align="start">
                <InviteSection job={job} />
                <Column gap={10} pt={12}>
                  <ShiftDetailsInfo job={job} />
                  {shouldShowRepostButton && (
                    <Flex justify="center">
                      <Button
                        startIcon={<OutlineRepostIcon size={24} />}
                        color="secondary"
                        size="large"
                        data-cy="repost-as-da-button"
                        onClick={repostEfdaAsDa}
                      >
                        Repost as DA
                      </Button>
                    </Flex>
                  )}
                </Column>
              </Grid>
            </Box>
          </Flex>
        </Screen.Body>
      </Screen>

      {cancelJobPopup && (
        <ConfirmPopup
          Icon={() => <AnimatedCloseCircle />}
          title="Are you sure you want to cancel?"
          description="Cancelling an open shift means that it will no longer be available to professionals around your office to accept and potentially work."
          leftButtonText="Go Back"
          leftButtonAction={() => setCancelJobPopup(false)}
          leftButtonDataCy="cancel-popup-go-back"
          rightButtonText="Cancel Shift"
          rightButtonDataCy="confirm-cancel-shift"
          rightButtonAction={() => {
            cancelJob({ jobId: job.id, redirect: true });
            setCancelJobPopup(false);
          }}
        />
      )}
    </>
  );
}
